import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { RiMenu3Fill } from "react-icons/ri";
import { FaXmark } from 'react-icons/fa6';
import { FaAngleDown } from 'react-icons/fa';
import { SiVerizon } from 'react-icons/si';
import { t } from 'i18next';

export const Navbar = () => {
  const { i18n } = useTranslation();
  const language = i18n.language;

  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeNavItem, setActiveNavItem] = useState('');
  const location = useLocation();

  // Detect Active Route
  useEffect(() => {
    const path = location.pathname;
    const navItem = path === '/' ? 'Home' : path.replace('/', '');
    setActiveNavItem(navItem);
  }, [location]);

  // Detect scroll for transparent → solid navbar
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenuLanguage = () => setLanguageMenuOpen(!languageMenuOpen);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);
  const toggleCloseMenuNavigation = () => {
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const handleLanguageClick = (lng) => {
    i18n.changeLanguage(lng);
    setLanguageMenuOpen(false);
    window.location.pathname = "/";
  };

  return (
    <nav
     className={`
    fixed top-0 left-0 w-full z-50 transition-all duration-500

    ${isScrolled 
      ? 'bg-black/70 shadow-xl' 
      : 'bg-gradient-to-r from-black/60 via-black/40 to-black/60'
    }

    border-b border-white/10
    ${language === 'en' || language === 'en-us' ? 'font-serif' : 'font-speda'}
  `}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex justify-between items-center">

        {/* Logo */}
      
        {/* Desktop Nav */}
        <div
          dir={`${language === 'en' || language === 'en-us' ? 'ltr' : 'rtl'}`}
          className="hidden md:flex text-white text-lg uppercase"
        >
          {[
            { path: '/', key: 'Home', label: t('navbarTxt.home') },
            { path: '/product', key: 'product', label: t('navbarTxt.products') },
            { path: '/service', key: 'service', label: t('navbarTxt.service') },
            { path: '/about', key: 'about', label: t('navbarTxt.about') },
            { path: '/Contact', key: 'Contact', label: t('navbarTxt.contact') },
            { path: '/career', key: 'career', label: t('navbarTxt.career') }
          ].map((item) => (
            <Link
              key={item.key}
              to={item.path}
              onClick={() => window.scrollTo(0, 0)}
              className={`relative pb-1 mx-4 transition-all duration-300 hover:text-yellow-400
                ${activeNavItem === item.key ? 'text-yellow-400' : 'text-white'}`}
            >
              {item.label}
            
            </Link>
          ))}
        </div>

        {/* Language Selector */}
        <div className="relative text-white hidden md:block cursor-pointer select-none">
          <div onClick={toggleMenuLanguage} className="flex items-center gap-1 text-lg uppercase">
            {language}
            <FaAngleDown size={18} />
          </div>

          {languageMenuOpen && (
            <div className="absolute right-0 mt-3 bg-black/80 border border-white/20 rounded-lg shadow-lg p-3 space-y-2 w-32 backdrop-blur-lg">
              <div className="flex justify-between items-center hover:text-yellow-300" onClick={() => handleLanguageClick('en')}>
                <span>English</span>
                {language === 'en' && <SiVerizon />}
              </div>
              <div className="flex justify-between items-center hover:text-yellow-300" onClick={() => handleLanguageClick('ar')}>
                <span>عربی</span>
                {language === 'ar' && <SiVerizon />}
              </div>
              <div className="flex justify-between items-center hover:text-yellow-300" onClick={() => handleLanguageClick('kr')}>
                <span>کوردی</span>
                {language === 'kr' && <SiVerizon />}
              </div>
            </div>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden text-3xl text-white"
          onClick={toggleMenu}
        >
          <RiMenu3Fill />
        </button>
      </div>

      {/* Mobile Full-Screen Menu */}
      {isMenuOpen && (
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          transition={{ type: 'spring', stiffness: 40, damping: 12 }}
          className="fixed top-0 right-0 w-72 h-full bg-gray-600 bg-gradient-to-t from-black/70 to-black/80 backdrop-blur-2xl backdrop-blur-2xl z-[999] flex flex-col items-center pt-24 space-y-6 text-white text-xl uppercase shadow-2xl"
        >
          <button className="absolute top-6 right-6 text-3xl" onClick={closeMenu}>
            <FaXmark />
          </button>

          {[
            { path: '/', key: 'Home', label: t('navbarTxt.home') },
            { path: '/about', key: 'about', label: t('navbarTxt.about') },
            { path: '/Contact', key: 'Contact', label: t('navbarTxt.contact') },
            { path: '/service', key: 'service', label: t('navbarTxt.service') },
            { path: '/product', key: 'product', label: t('navbarTxt.products') },
            { path: '/career', key: 'career', label: t('navbarTxt.career') }
          ].map((item) => (
            <Link
              key={item.key}
              to={item.path}
              onClick={toggleCloseMenuNavigation}
              className={`$${activeNavItem === item.key ? 'text-yellow-400' : ''}`}
            >
              {item.label}
            </Link>
          ))}

          {/* Mobile Language Section */}
         <div className="pt-6 border-t items-center justify-center border-yellow-500 w-3/4 flex flex-col">
  <p className="text-center text-yellow-500 mb-3">
    {t('navbarTxt.language')}
  </p>

  <div className="flex flex-col items-center space-y-2 text-lg">
    <span className="cursor-pointer hover:text-yellow-300" onClick={() => handleLanguageClick('en')}>English</span>
    <span className="cursor-pointer hover:text-yellow-300" onClick={() => handleLanguageClick('ar')}>عربی</span>
    <span className="cursor-pointer hover:text-yellow-300" onClick={() => handleLanguageClick('kr')}>کوردی</span>
  </div>
</div>

        </motion.div>
      )}
    </nav>
  );
};
